import { Radio, Stack } from '@mantine/core'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { Sizes } from '~/assets/style/tokens'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { MethodCard } from '~/components/MethodCard'
import { SITE_PRODUCT_CHOICES, SITE_PRODUCT_DESCRIPTION, SITE_PRODUCT_LABEL, SiteProduct, SiteProductAvailability, siteProductDisabledReason, siteProductUpgradeOffers } from './siteProduct'

type Props<T extends FieldValues> = {
  control: Control<T>
  name: FieldPath<T>
  /** What the media runs on today, and `undefined` at creation, where nothing has been picked yet. */
  currentProduct?: SiteProduct
  availability: Omit<SiteProductAvailability, 'currentProduct'>
  trackingLabelOf: (product: SiteProduct) => TrackingLabel
  /** Products open to Pubstack staff alone on this scope, marked with a shield. */
  staffOnlyProducts?: SiteProduct[]
}

/** One card per product. Every product stays on screen even when out of reach — a disabled card says why it is closed. */
export const SiteProductPicker = <T extends FieldValues>({ control, name, currentProduct, availability, trackingLabelOf, staffOnlyProducts = [] }: Props<T>) => {
  const { field } = useController({ control, name })

  const disabledReasonFor = (product: SiteProduct): string | undefined => siteProductDisabledReason(product, { ...availability, currentProduct })

  return (
    <Stack gap={Sizes[16]}>
      <Radio.Group value={field.value} onChange={field.onChange}>
        <Stack gap={Sizes[10]}>
          {SITE_PRODUCT_CHOICES.map((product) => (
            <MethodCard
              key={product}
              value={product}
              title={SITE_PRODUCT_LABEL[product]}
              description={SITE_PRODUCT_DESCRIPTION[product]}
              disabledReason={disabledReasonFor(product)}
              // A product the scope has not bought is sold rather than padlocked — see `siteProductUpgradeOffers`.
              upgradeOffers={siteProductUpgradeOffers(disabledReasonFor(product))}
              isStaffOnly={staffOnlyProducts.includes(product)}
              trackingLabel={trackingLabelOf(product)}
            />
          ))}
        </Stack>
      </Radio.Group>
      {/* That the choice is final is said on save, in the confirmation that asks for the media name typed back. */}
    </Stack>
  )
}
